import { FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { AttendanceRecordSource } from '@schooly/analytics';
import {
  SchoolYear,
  StudentRegistration,
  TimetableStructure,
  useGetEnrollments,
  useGetTimetableStructuresQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useCellStates } from '@schooly/components/data-grid';
import { SchoolUserRole } from '@schooly/constants';
import { DROPDOWN } from '@schooly/constants/test-ids';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { DropdownYears, Loading, SelectPopover } from '@schooly/style';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import { NoSearchResultsFound } from '../../../components/common/NoSearchResultsFound/NoSearchResultsFound';
import useSchoolYears from '../../../hooks/useSchoolYears';
import { AttendanceGridShell } from './AttendanceSidebar/AttendanceGridShell';
import { AttendanceStudentStatsSummary } from './AttendanceStudentStatsSummary';
import { AttendanceTrackingPointsView } from './AttendanceTrackingPointsView';
import { AttendanceWeekdayHeaderCell } from './common/AttendanceCells';
import { AttendanceGrid } from './common/AttendanceGrid';
import { ATTENDANCE_STUDENT_STYLING } from './common/attendanceStyling';
import { AttendanceCellState, AttendanceGridColumn, AttendanceGridStudent } from './common/types';
import { useAttendanceGridForStudent } from './data/useAttendanceGridForStudent';
import { useAttendanceTableControls } from './data/useAttendanceTableControls';

const renderWeekdayHeader = (column: AttendanceGridColumn) => (
  <AttendanceWeekdayHeaderCell column={column} />
);

const AttendanceSessionView: FC<{
  student: AttendanceGridStudent;
  selectedYear: SchoolYear | undefined;
}> = ({ student, selectedYear }) => {
  const { formatMessage } = useIntl();
  const cellContext = useCellStates<AttendanceCellState>();

  const { columns, rows, codes, counts, justificationCounts, isLoading } =
    useAttendanceGridForStudent(student, selectedYear);

  const renderContent = () => {
    if (isLoading) return <Loading />;

    if (rows.length === 0) return <NoSearchResultsFound type="small" />;

    return (
      <AttendanceGrid
        rows={rows}
        gridColumns={columns}
        styling={ATTENDANCE_STUDENT_STYLING}
        primaryHeaderLabel={formatMessage({ id: 'attendance-Week' })}
        renderDayHeader={renderWeekdayHeader}
        getCellState={cellContext.getCellState}
      />
    );
  };

  return (
    <AttendanceGridShell codes={codes} source={AttendanceRecordSource.Student}>
      {(isLoading || rows.length > 0) && (
        <AttendanceStudentStatsSummary
          counts={counts}
          justificationCounts={justificationCounts}
          loading={isLoading}
        />
      )}
      {renderContent()}
    </AttendanceGridShell>
  );
};

const AttendanceStructureSelect: FC<{
  structures: TimetableStructure[];
  selectedStructure: TimetableStructure | undefined;
  onSelect: (id: string) => void;
}> = ({ structures, selectedStructure, onSelect }) => {
  const { formatMessage } = useIntl();

  if (!selectedStructure || structures.length === 0) return null;

  return (
    <Stack direction="row" alignItems="center" gap={0.5} minWidth={0}>
      <Typography variant="h3" color="common.grey2" noWrap flexShrink={0}>
        {formatMessage({ id: 'section-Structure' })}:
      </Typography>
      {structures.length === 1 ? (
        <Typography variant="h3" noWrap minWidth={0}>
          {selectedStructure.name}
        </Typography>
      ) : (
        <Typography variant="h3" noWrap minWidth={0}>
          <SelectPopover
            options={structures}
            selectedOption={selectedStructure}
            onSelect={(structure) => onSelect(structure.id)}
            dataTestId={DROPDOWN.TIMETABLE_STRUCTURE}
            popoverProps={{
              sx: {
                '& .MuiList-root .MuiMenuItem-root': {
                  whiteSpace: 'normal',
                  '& > .MuiStack-root': { alignItems: 'center' },
                },
              },
            }}
          />
        </Typography>
      )}
    </Stack>
  );
};

const AttendanceTableForStudentContent: FC<{
  student: AttendanceGridStudent;
  perPeriodAvailable: boolean;
  schoolYears: SchoolYear[];
  defaultValidity: SchoolYear | undefined;
  structures: TimetableStructure[];
  enrollments: StudentRegistration[];
}> = ({ student, perPeriodAvailable, schoolYears, defaultValidity, structures, enrollments }) => {
  const { formatMessage } = useIntl();
  const controls = useAttendanceTableControls({
    perPeriodAvailable,
    schoolYears,
    defaultValidity,
    structures,
    enrollments,
  });

  const isPerPeriod = controls.viewMode === 'per-period';

  return (
    <Stack direction="column" gap={2} sx={{ height: '100%', overflow: 'hidden' }}>
      <Stack
        direction="row"
        gap={1}
        justifyContent="space-between"
        alignItems="center"
        minWidth={0}
      >
        <Typography variant="h2" flexShrink={0}>
          {formatMessage({ id: 'section-Attendance' })}
        </Typography>
        <Stack direction="row" alignItems="center" gap={2.5} minWidth={0}>
          {isPerPeriod && (
            <AttendanceStructureSelect
              structures={controls.structures}
              selectedStructure={controls.selectedStructure}
              onSelect={controls.setSelectedStructureId}
            />
          )}
          {perPeriodAvailable && (
            <FormControlLabel
              sx={{ m: 0, flexShrink: 0 }}
              label={formatMessage({ id: 'attendance-PerPeriod' })}
              control={
                <Switch
                  sx={{
                    ' .MuiSwitch-track, .Mui-checked+.MuiSwitch-track': {
                      opacity: 1,
                      backgroundColor: 'background.paper',
                    },
                    '&:hover': {
                      color: 'primary.main',
                      ' .MuiSwitch-track, .Mui-checked+.MuiSwitch-track': {
                        opacity: 1,
                      },
                    },
                  }}
                  checked={isPerPeriod}
                  onChange={(_, checked) =>
                    controls.setViewMode(checked ? 'per-period' : 'session')
                  }
                />
              }
            />
          )}
          <Stack flexShrink={0}>
            <DropdownYears
              years={controls.availableYears}
              defaultYear={controls.defaultYear}
              currentYear={controls.selectedYear}
              onYearChange={controls.setSchoolYear}
            />
          </Stack>
        </Stack>
      </Stack>

      <Stack sx={{ gap: 2, flex: 1, overflow: 'hidden' }}>
        {isPerPeriod ? (
          <AttendanceTrackingPointsView
            student={student}
            selectedYear={controls.selectedYear}
            timetableStructureId={controls.selectedStructure?.id}
          />
        ) : (
          <AttendanceSessionView student={student} selectedYear={controls.selectedYear} />
        )}
      </Stack>
    </Stack>
  );
};

export const AttendanceTableForStudent: FC<{
  student: AttendanceGridStudent;
}> = ({ student }) => {
  const { schoolId = '' } = useAuth();
  const { activeAgeGroups, isLoading: ageGroupsLoading } = useAgeGroups(
    { schoolId, userType: SchoolUserRole.Student },
    { enabled: !!schoolId, refetchOnMount: 'always' },
  );
  const { defaultValidity, schoolYears, isLoading: schoolYearsLoading } = useSchoolYears();
  const { data: structuresData, isLoading: structuresLoading } = useGetTimetableStructuresQuery(
    { school_id: schoolId },
    { enabled: !!schoolId, refetchOnMount: 'always' },
  );
  const { data: enrollmentsData, isInitialLoading: enrollmentsLoading } = useGetEnrollments(
    { schoolId, studentId: student.relation_id },
    { enabled: !!schoolId && !!student.relation_id, refetchOnMount: 'always' },
  );

  const isLoading =
    ageGroupsLoading || schoolYearsLoading || structuresLoading || enrollmentsLoading;

  if (isLoading) return <Loading />;

  const perPeriodAvailable = activeAgeGroups.some(
    (ageGroup) => ageGroup.attendance_frequency?.per_period_enabled,
  );

  return (
    <AttendanceTableForStudentContent
      student={student}
      perPeriodAvailable={perPeriodAvailable}
      schoolYears={schoolYears}
      defaultValidity={defaultValidity}
      structures={structuresData?.results ?? []}
      enrollments={enrollmentsData?.enrollments ?? []}
    />
  );
};
